import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface CommissionViewPeriod {
  quarter: string;
  year: number;
}

interface CommissionViewPeriodContextValue {
  period: CommissionViewPeriod | null;
  setViewPeriod: (period: CommissionViewPeriod) => void;
}

const CommissionViewPeriodContext = createContext<CommissionViewPeriodContextValue | null>(null);

export function CommissionViewPeriodProvider({ children }: { children: React.ReactNode }) {
  const [period, setPeriod] = useState<CommissionViewPeriod | null>(null);
  const setViewPeriod = useCallback((next: CommissionViewPeriod) => {
    setPeriod(next);
  }, []);
  const value = useMemo(
    () => ({ period, setViewPeriod }),
    [period, setViewPeriod]
  );
  return (
    <CommissionViewPeriodContext.Provider value={value}>
      {children}
    </CommissionViewPeriodContext.Provider>
  );
}

export function useCommissionViewPeriod(): CommissionViewPeriodContextValue {
  const ctx = useContext(CommissionViewPeriodContext);
  if (!ctx) {
    throw new Error("useCommissionViewPeriod must be used within CommissionViewPeriodProvider");
  }
  return ctx;
}

/** Report the quarter/year currently shown in a commission sub-view (for the load-invoices modal). */
export function useReportCommissionViewPeriod(period: CommissionViewPeriod | null): void {
  const { setViewPeriod } = useCommissionViewPeriod();
  React.useEffect(() => {
    if (period) {
      setViewPeriod(period);
    }
  }, [period, setViewPeriod]);
}

export function monthYearToQuarterPeriod(month: number, year: number): CommissionViewPeriod {
  const quarter = Math.ceil(month / 3);
  return { quarter: `Q${quarter}`, year };
}
